import {openDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {head, isNil, mergeAll} from 'ramda';
import {isArray, isString} from 'ramda-adjunct';

import {TenantResponseBody} from '@dms/api/shared';
import {GetWarehouseResponse, ReceiveNoteState} from '@dms/api/warehouse';
import {testIds} from '@dms/routes';
import {
  DmsDataGrid,
  PriceListRowData,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  logError,
} from '@dms/shared';
import {ArticleCreateDefaultValues, PriceListDetail} from '@dms/shared-aftersales';
import {FullscreenDataGridWrapper} from '@dms/warehouse/shared-components';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {ReplaceArticlePricelistTabExternalFilters} from './ReplaceArticlePricelistTabExternalFilters';

const PRICE_LIST_EXTERNAL_FILTER_ID = 'replace-article-price-list-external-filter';
const PREDEFINED_COLUMNS = [
  'name',
  'catalogNumber',
  'orderingNumber',
  'manufacturerId',
  'supplierId',
  'supplierAccount',
  'supplierOrderType',
  'branchId',
  'authorizationProfileId',
  'purchasePrice',
  'recommendedPrice',
  'supplierDiscountGroup',
  'supplierOrderingNumber',
  'supplierManufacturerName',
  'supplierManufacturerCode',
  'standardOrderQuantity',
  'localStock',
  'totalStock',
] as const;

interface ReplaceArticlePricelistTabProps extends RequiredTestIdProps {
  warehouseId: string | Nullish;
  authorizationProfileId: string | Nullish;
  branchId: string | Nullish;
  receiveNoteId: string | Nullish;
  warehouse: GetWarehouseResponse | Nullish;
  tenant: TenantResponseBody | Nullish;
  state: ReceiveNoteState | Nullish;
  onSelectArticle: (articleId: string) => void;
  onCreateArticleFromPricelist: (defaultValues: ArticleCreateDefaultValues) => void;
  hasPendingRequest: boolean;
}

export function ReplaceArticlePricelistTab(props: ReplaceArticlePricelistTabProps) {
  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([
      filter,
      {
        branchId: props.branchId,
        authorizationProfileId: props.authorizationProfileId,
      },
    ]);

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
  }) => {
    const priceListId = isArray(rowId) ? head(rowId) : rowId;

    const catalogNumber = getColumnData<string>('catalogNumber')?.value;
    const name = getColumnData<string>('name')?.value;
    const manufacturer = getColumnData<{label: string; id: string}>('manufacturerId')?.value;

    if (!isString(catalogNumber)) {
      return logError('Catalog number is not defined');
    }

    if (!isString(name)) {
      return logError('Name is not defined');
    }

    if (!isString(manufacturer?.id)) {
      return logError('Manufacturer id is not defined');
    }

    if (isNil(priceListId)) {
      return logError('priceListId was not defined');
    }

    const priceListRowData = {
      name: {value: getColumnData('name')?.value},
      catalogNumber: {value: getColumnData('catalogNumber')?.value},
      supplierOrderingNumber: {value: getColumnData('supplierOrderingNumber')?.value},
      manufacturerId: {value: getColumnData('manufacturerId')?.value},
      supplierId: {value: getColumnData('supplierId')?.value},
      purchasePrice: {value: getColumnData('purchasePrice')?.value},
      recommendedPrice: {value: getColumnData('recommendedPrice')?.value},
      supplierDiscountGroup: getColumnData('supplierDiscountGroup')?.value,
      supplierManufacturerName: getColumnData('supplierManufacturerName')?.value,
      supplierManufacturerCode: getColumnData('supplierManufacturerCode')?.value,
      standardOrderQuantity: getColumnData('standardOrderQuantity')?.value,
      localStock: getColumnData('localStock')?.value,
      totalStock: getColumnData('totalStock')?.value,
      supplierAccountCode: getColumnData<string>('supplierAccount', {isOptional: true})?.value,
      supplierOrderTypeCode: getColumnData<string>('supplierOrderType', {isOptional: true})?.value,
      branchId: getColumnData<string>('branchId', {isOptional: true})?.value,
      authorizationProfileId: getColumnData<string>('authorizationProfileId', {isOptional: true})
        ?.value,
    } as PriceListRowData;

    match(actionKey)
      .with('redirectDetail', () => {
        openDialog(
          <PriceListDetail
            id={priceListId}
            priceListRowData={priceListRowData}
            data-testid={testIds.warehouse.priceList('dialogs.detail')}
          />,
          {
            id: 'detailDialog',
            title: priceListRowData?.name?.value,
            size: 'large',
            withAdditionalFooter: true,
          }
        );
      })
      .with('addToBasket', () => {
        const defaultValues: ArticleCreateDefaultValues = {
          catalogNumber,
          name,
          manufacturerId: manufacturer.id,
          warehouseId: props.warehouse?.id,
        };

        props.onCreateArticleFromPricelist(defaultValues);
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <FullscreenDataGridWrapper
      externalFilterId={PRICE_LIST_EXTERNAL_FILTER_ID}
      hideCardWrapper
      data-testid={props['data-testid']}
    >
      <DmsDataGrid
        gridCode={WAREHOUSE_GRID_CODES.priceListBasket}
        queryModifier={queryModifier}
        externalFilterId={PRICE_LIST_EXTERNAL_FILTER_ID}
        predefinedColumns={PREDEFINED_COLUMNS}
        actionCallback={actionCallback}
        filterComponent={ReplaceArticlePricelistTabExternalFilters}
        _useAsLastResort_definitionBehaviorOverrides={{filterMode: 'NATIVE'}}
        data-testid={suffixTestId('dataGrid', props)}
      />
    </FullscreenDataGridWrapper>
  );
}
